import { Canvas, useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { useEffect, useRef } from "react";
import EngineModel from "./EngineModel";
import ParticleField from "./ParticleField";
import { scrollStore } from "@/utils/scrollStore";

function ss(a, b, x) { const t = Math.max(0, Math.min(1, (x - a) / (b - a))); return t * t * (3 - 2 * t); }

function SceneSetup({ onProgress }) {
  const { scene } = useThree();
  useEffect(() => {
    onProgress?.(15);
    scene.fog = new THREE.Fog("#030406", 14, 32);

    // Key — strong warm directional from upper-right (specular highlights)
    const key = new THREE.DirectionalLight(0xf0e8d8, 0.7);
    key.position.set(4, 5, 5);
    // Fill — cool blue from left (shadow detail, subtle)
    const fill = new THREE.PointLight(0x283848, 0.1, 30);
    fill.position.set(-7, 1, -3);
    // Rim — cyan edge separation from behind
    const rim = new THREE.PointLight(0x00e0f0, 0.4, 28);
    rim.position.set(0, 0, -8);
    // Top — subtle overhead for horizontal surfaces
    const top = new THREE.PointLight(0xd8d0c8, 0.1, 22);
    top.position.set(0, 9, 0);
    // Under fill — very subtle warm bounce from base
    const under = new THREE.PointLight(0x181210, 0.06, 12);
    under.position.set(0, -3, 2);
    // Hemisphere (ground bounce — very subtle)
    const hemi = new THREE.HemisphereLight(0x0a1018, 0x020304, 0.04);
    // Ambient — near-zero for deep shadows and occlusion
    const ambient = new THREE.AmbientLight(0xffffff, 0.012);

    scene.add(key, fill, rim, top, under, hemi, ambient);
    onProgress?.(30);
    return () => { scene.remove(key, fill, rim, top, under, hemi, ambient); scene.fog = null; };
  }, [scene, onProgress]);
  return null;
}

function CameraController() {
  useFrame(({ camera, size }) => {
    const p = scrollStore.progress;
    const ign = scrollStore.igniting;
    const mobile = size.width < 768 ? 1 : 0;
    const zBoost = mobile * 2.6;

    /* Choreographed camera:
       0.0 → 0.25  wide idle shot
       0.25 → 0.55 orbit to the side (sees chip in profile + token rim)
       0.55 → 0.80 rise up for a top-down on the QVX die
       0.80 → 1.0  pull back for the "full activation" hero
    */
    const orbitT  = ss(0.15, 0.55, p);                 // 0→1 orbit progression
    const topT    = ss(0.55, 0.82, p);                 // 0→1 top-down tilt
    const heroT   = ss(0.82, 1.0, p);                  // 0→1 final pull-back

    // Orbit around the engine: camera angle sweeps from front-right → left
    const orbitAngle = orbitT * Math.PI * 0.9;         // ~160° sweep
    const orbitR = 7.0 - orbitT * 0.6;                 // slight pull in during orbit

    const targetZ = (7.2 + zBoost)
      - orbitT * (7.2 - Math.cos(orbitAngle) * orbitR)
      - topT * 1.3
      + heroT * 2.4
      - (ign ? 3 : 0);
    const orbitX = Math.sin(orbitAngle) * orbitR;
    const targetX = 0.9 - mobile * 0.8
      + orbitT * (orbitX - 0.9)
      - topT * 0.8
      + heroT * 0.4;
    const targetY = 2.1
      + orbitT * 0.2
      + topT * 2.6          // rise high for top-down
      - heroT * 1.6;        // come back down for hero finale

    camera.position.z += (targetZ - camera.position.z) * 0.04;
    camera.position.y += (targetY - camera.position.y) * 0.04;
    camera.position.x += (targetX - camera.position.x) * 0.04;

    // Look target drifts from slightly-below-origin (idle)
    // → center (orbit) → up-at-chip (top-down) → chassis (hero)
    const lookY = -0.2
      + orbitT * 0.4
      + topT * 0.5
      - heroT * 0.7;
    camera.lookAt(0, lookY, 0);
  });
  return null;
}

function ReadyDetector({ onProgress, onReady }) {
  const fired = useRef(false);
  useFrame(() => {
    if (!fired.current) {
      fired.current = true;
      onProgress?.(95);
      setTimeout(() => { onProgress?.(100); onReady?.(); }, 400);
    }
  });
  return null;
}

export default function EngineScene({ onProgress, onReady }) {
  return (
    <Canvas
      camera={{ position: [1.0, 2.4, 7.5], fov: 40 }}
      dpr={[1, 1.5]}
      gl={{ antialias: true, alpha: false }}
      style={{ background: "#030406" }}
      onCreated={() => onProgress?.(10)}
    >
      <SceneSetup onProgress={onProgress} />
      <EngineModel onProgress={onProgress} />
      <ParticleField onProgress={onProgress} />
      <CameraController />
      <ReadyDetector onProgress={onProgress} onReady={onReady} />
    </Canvas>
  );
}
